import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/company';
import { motion, AnimatePresence } from 'framer-motion';

// Navigation structure with dropdowns
const navigationItems = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  {
    name: 'Solutions',
    children: [
      { type: 'header', label: 'Services Overview' },
      { name: 'All Services', href: '/services' },
      { type: 'separator' },
      { type: 'header', label: 'Service Shortcuts' },
      { name: 'Unified Communications', href: '/services/unified-communications' },
      { name: 'Networks', href: '/services/networks' },
      { name: 'Technology Infrastructure', href: '/services/technology-infrastructure' },
      { name: 'Cyber Security', href: '/services/cyber-security' },
      { name: 'AV, Automation & Control', href: '/services/av-automation-control' },
      { name: 'Consulting', href: '/services/consulting' },
      { name: 'Renewable Energy (Solar)', href: '/services/renewable-energy' },
      { type: 'separator' },
      { type: 'header', label: 'Catalog' },
      { name: 'Products', href: '/products' },
    ],
  },
  {
    name: 'Company',
    children: [
      { name: 'Partnerships', href: '/partnerships' },
      // { name: 'Certifications', href: '/certifications' },
      { name: 'Clients', href: '/clients' },
    ],
  },
  { name: 'Resources', href: '/resources' },
  { name: 'Contact', href: '/contact' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    // Check initial scroll position
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setIsOpen(false);
      setOpenDropdown(null);
      setMobileAccordion(null);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Check if a route is active (including child routes)
  const isRouteActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  // Check if any child route is active
  const hasActiveChild = (children) => {
    if (!children) return false;
    return children.some((child) => child.href && isRouteActive(child.href));
  };

  const handleMouseEnter = (name) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const handleKeyDown = (e, name, hasChildren) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (hasChildren) {
        e.preventDefault();
        setOpenDropdown(openDropdown === name ? null : name);
      }
    }
    if (e.key === 'Escape') {
      setOpenDropdown(null);
    }
  };

  // Text color classes based on scroll state
  const textColorClass = scrolled
    ? 'text-foreground/80 hover:text-primary'
    : 'text-primary-foreground/90 hover:text-primary-foreground';

  const activeTextColorClass = scrolled
    ? 'text-primary bg-primary/10'
    : 'text-accent bg-accent/20';

  const dropdownTextClass = scrolled
    ? 'text-foreground/80 hover:text-primary hover:bg-primary/5'
    : 'text-foreground/80 hover:text-primary hover:bg-primary/5';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-card/95 backdrop-blur-md shadow-lg border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigationItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.children && handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                {item.children ? (
                  // Dropdown trigger
                  <button
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      hasActiveChild(item.children)
                        ? activeTextColorClass
                        : textColorClass
                    }`}
                    onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                    onKeyDown={(e) => handleKeyDown(e, item.name, true)}
                    aria-expanded={openDropdown === item.name}
                    aria-haspopup="true"
                  >
                    {item.name}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        openDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                ) : (
                  // Regular link
                  <Link
                    to={item.href}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isRouteActive(item.href)
                        ? activeTextColorClass
                        : textColorClass
                    }`}
                  >
                    {item.name}
                  </Link>
                )}

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.children && openDropdown === item.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 w-64 py-2 bg-card rounded-xl border border-border shadow-xl"
                      onMouseEnter={() => handleMouseEnter(item.name)}
                      onMouseLeave={handleMouseLeave}
                    >
                      {item.children.map((child, idx) => {
                        if (child.type === 'header') {
                          return (
                            <div
                              key={idx}
                              className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                            >
                              {child.label}
                            </div>
                          );
                        }
                        if (child.type === 'separator') {
                          return (
                            <div key={idx} className="my-2 border-t border-border" />
                          );
                        }
                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              isRouteActive(child.href)
                                ? 'text-primary bg-primary/10 font-medium'
                                : dropdownTextClass
                            }`}
                          >
                            {child.name}
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              asChild
              variant={scrolled ? 'hero' : 'hero-accent'}
              className={!scrolled ? 'shadow-lg' : ''}
            >
              <Link to="/contact">Request Consultation</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled
                ? 'hover:bg-secondary text-foreground'
                : 'hover:bg-primary-foreground/10 text-primary-foreground'
            }`}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden bg-card rounded-b-xl border-t border-border/50 shadow-xl max-h-[70vh] overflow-y-auto"
            >
              <div className="py-4 space-y-1">
                {navigationItems.map((item) => (
                  <div key={item.name}>
                    {item.children ? (
                      // Mobile accordion
                      <div>
                        <button
                          onClick={() =>
                            setMobileAccordion(
                              mobileAccordion === item.name ? null : item.name
                            )
                          }
                          className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                            hasActiveChild(item.children)
                              ? 'text-primary bg-primary/10'
                              : 'text-foreground/80 hover:text-primary hover:bg-primary/5'
                          }`}
                        >
                          {item.name}
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 ${
                              mobileAccordion === item.name ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {mobileAccordion === item.name && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pl-4 py-2 space-y-1">
                                {item.children.map((child, idx) => {
                                  if (child.type === 'header') {
                                    return (
                                      <div
                                        key={idx}
                                        className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                                      >
                                        {child.label}
                                      </div>
                                    );
                                  }
                                  if (child.type === 'separator') {
                                    return (
                                      <div
                                        key={idx}
                                        className="my-2 mx-4 border-t border-border"
                                      />
                                    );
                                  }
                                  return (
                                    <Link
                                      key={child.href}
                                      to={child.href}
                                      className={`block px-4 py-2 text-sm rounded-lg transition-colors ${
                                        isRouteActive(child.href)
                                          ? 'text-primary bg-primary/10 font-medium'
                                          : 'text-foreground/70 hover:text-primary hover:bg-primary/5'
                                      }`}
                                    >
                                      {child.name}
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                          isRouteActive(item.href)
                            ? 'text-primary bg-primary/10'
                            : 'text-foreground/80 hover:text-primary hover:bg-primary/5'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="pt-4 px-4">
                  <Button asChild variant="hero" className="w-full">
                    <Link to="/contact">Request Consultation</Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
